import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/crm');
      return;
    }

    const checkStatus = async () => {
      if (attempts >= 5) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/subscription/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setTimeout(() => {
            window.location.href = '/crm';
          }, 2000);
        } else if (response.data.status === 'expired') {
          setStatus('failed');
        } else {
          setAttempts(attempts + 1);
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking status:', error);
        setAttempts(attempts + 1);
        setTimeout(checkStatus, 2000);
      }
    };

    checkStatus();
  }, [searchParams, navigate, attempts]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F9F9F7] p-6">
      <div className="noise-overlay" />
      <Card className="w-full max-w-md border-gray-100 shadow-lg text-center">
        <CardHeader className="pb-4">
          <div className="flex justify-center mb-4">
            {status === 'checking' ? (
              <Loader2 className="w-16 h-16 text-[#1A4D2E] animate-spin" />
            ) : status === 'success' ? (
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-3xl">❌</span>
              </div>
            )}
          </div>
          <CardTitle className="text-2xl font-bold">
            {status === 'checking' && 'Processing Payment...'}
            {status === 'success' && 'Payment Successful!'}
            {status === 'failed' && 'Payment Failed'}
            {status === 'timeout' && 'Taking Longer Than Expected'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'checking' && (
            <p className="text-gray-600">Please wait while we confirm your subscription...</p>
          )}
          {status === 'success' && (
            <>
              <p className="text-gray-600">Your subscription is now active!</p>
              <p className="text-sm text-gray-500">Redirecting to your CRM...</p>
            </>
          )}
          {(status === 'failed' || status === 'timeout') && (
            <>
              <p className="text-gray-600">
                There was an issue processing your payment. Please try again or contact support.
              </p>
              <Button
                onClick={() => navigate('/crm')}
                className="w-full rounded-full bg-[#1A4D2E] hover:bg-[#14532D]"
              >
                Back to Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}